import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faEye,
	faFile,
	faChevronLeft,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import { fetchCalendarEntretiens } from "../../store/actions/entretienActions"
import { createCalendar } from "../../utilities/Addons"
import { COLORS } from "../../utilities/Constants"
import { STATUTS, dotStyle } from "./entretienUtils"

// Cycle clos → « fait » ; sinon statut d'échéance (EntretienCycleSerializer).
const statutCal = (ent) =>
	ent.done ? STATUTS.fait : STATUTS[ent.statut] || STATUTS.a_venir

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]
const MONTHS = [
	"Janvier",
	"Février",
	"Mars",
	"Avril",
	"Mai",
	"Juin",
	"Juillet",
	"Août",
	"Septembre",
	"Octobre",
	"Novembre",
	"Décembre",
]

const styles = {
	nav: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 10,
		marginBottom: 16,
	},
	navArrow: {
		width: 38,
		height: 38,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: 9,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		cursor: "pointer",
		fontFamily: "inherit",
	},
	navCurrent: {
		minWidth: 180,
		padding: "8px 18px",
		borderRadius: 9,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 14,
		fontWeight: 700,
		cursor: "pointer",
		fontFamily: "inherit",
		textAlign: "center",
		textTransform: "capitalize",
	},
	grid: {
		backgroundColor: COLORS.surface,
		borderRadius: 12,
		overflow: "hidden",
		boxShadow:
			"0 1px 2px rgba(28, 25, 23, 0.04), 0 6px 20px rgba(28, 25, 23, 0.06)",
	},
	weekHead: {
		display: "grid",
		// minmax(0, 1fr) : un libellé long ne doit pas élargir sa colonne
		// (1fr = minmax(auto, 1fr) ne descend pas sous le min-content).
		gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
		backgroundColor: COLORS.bg,
		borderBottom: `1px solid ${COLORS.border}`,
	},
	dayHead: {
		padding: "8px 10px",
		fontSize: 11,
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		color: COLORS.textLight,
		textAlign: "center",
	},
	week: { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" },
	cell: {
		minHeight: 92,
		minWidth: 0,
		borderRight: `1px solid ${COLORS.border}`,
		borderBottom: `1px solid ${COLORS.border}`,
		padding: 6,
		verticalAlign: "top",
	},
	cellEmpty: { backgroundColor: COLORS.bg },
	dayNum: { fontSize: 12, fontWeight: 600, color: COLORS.textMuted },
	dayNumToday: {
		fontSize: 12,
		fontWeight: 800,
		color: COLORS.accent,
	},
	ent: {
		marginTop: 4,
		padding: "4px 6px",
		borderRadius: 6,
		fontSize: 11,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 4,
	},
	legend: {
		display: "flex",
		gap: 16,
		flexWrap: "wrap",
		justifyContent: "center",
		marginBottom: 12,
		fontSize: 12,
		color: COLORS.textMuted,
	},
	legendItem: { display: "inline-flex", alignItems: "center", gap: 6 },
	entText: {
		flex: 1,
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	entBtn: {
		flexShrink: 0,
		border: "none",
		background: "none",
		cursor: "pointer",
		color: COLORS.accent,
		padding: 0,
	},
}

const EntretienCalendrier = ({ voirFicheAtelier }) => {
	const today = new Date()
	const [selectedMonth, setSelectedMonth] = useState(today.getMonth())
	const [selectedYear, setSelectedYear] = useState(today.getFullYear())

	const { calendar } = useSelector((state) => state.entretienInfo)
	const dispatch = useDispatch()

	useEffect(() => {
		dispatch(
			fetchCalendarEntretiens({
				mois: selectedMonth + 1,
				annee: selectedYear,
			}),
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedMonth, selectedYear])

	const isToday = (day) =>
		today.getMonth() === selectedMonth &&
		today.getFullYear() === selectedYear &&
		today.getDate() === parseInt(day)

	const prevMonth = () => {
		if (selectedMonth === 0) {
			setSelectedMonth(11)
			setSelectedYear((y) => y - 1)
		} else {
			setSelectedMonth((m) => m - 1)
		}
	}
	const nextMonth = () => {
		if (selectedMonth === 11) {
			setSelectedMonth(0)
			setSelectedYear((y) => y + 1)
		} else {
			setSelectedMonth((m) => m + 1)
		}
	}
	const goToday = () => {
		setSelectedMonth(today.getMonth())
		setSelectedYear(today.getFullYear())
	}

	return (
		<div>
			<div style={styles.nav}>
				<button
					type="button"
					style={styles.navArrow}
					title="Mois précédent"
					onClick={prevMonth}
				>
					<FontAwesomeIcon icon={faChevronLeft} />
				</button>
				<button
					type="button"
					style={styles.navCurrent}
					title="Revenir au mois courant"
					onClick={goToday}
				>
					{MONTHS[selectedMonth]} {selectedYear}
				</button>
				<button
					type="button"
					style={styles.navArrow}
					title="Mois suivant"
					onClick={nextMonth}
				>
					<FontAwesomeIcon icon={faChevronRight} />
				</button>
			</div>

			<div style={styles.legend}>
				{["retard", "du", "a_venir", "fait"].map((k) => (
					<span key={k} style={styles.legendItem}>
						<span style={dotStyle(STATUTS[k].color)} />
						{STATUTS[k].label}
					</span>
				))}
			</div>

			<div style={styles.grid}>
				<div style={styles.weekHead}>
					{DAYS.map((day) => (
						<div key={`jour${day}`} style={styles.dayHead}>
							{day}
						</div>
					))}
				</div>
				{createCalendar(selectedMonth, selectedYear).map(
					(week, wIndex) => (
						<div key={`row${wIndex}`} style={styles.week}>
							{week.map((day, dIndex) => (
								<div
									key={`cell${wIndex}${dIndex}`}
									style={{
										...styles.cell,
										...(day === "" ? styles.cellEmpty : {}),
									}}
								>
									{day !== "" && (
										<div
											style={
												isToday(day)
													? styles.dayNumToday
													: styles.dayNum
											}
										>
											{day}
										</div>
									)}
									{(calendar[day] || []).map((ent) => (
										<div
											key={`ent${ent.id}`}
											style={{
												...styles.ent,
												backgroundColor:
													statutCal(ent).bg,
												borderLeft: `3px solid ${statutCal(ent).color}`,
											}}
											title={`${ent.label} - ${ent.vehicule}`}
										>
											<span style={styles.entText}>
												{ent.vehicule} · {ent.label}
											</span>
											<button
												style={styles.entBtn}
												onClick={() =>
													voirFicheAtelier({
														atelier: ent.atelier,
														id: ent.id,
													})
												}
											>
												<FontAwesomeIcon
													icon={
														ent.atelier
															? faEye
															: faFile
													}
												/>
											</button>
										</div>
									))}
								</div>
							))}
						</div>
					),
				)}
			</div>
		</div>
	)
}

export default EntretienCalendrier
